import { Button, ButtonGroup, Stack, Typography } from '@mui/material'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  AWSAuthenticationType,
  AWSRoleCreationMethod,
  getAWSAuthenticationTypes,
  getAWSRoleCreationMethods,
  isIamRoleAuthUnavailable
} from 'components/status-checks/authentication-form-utils'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import {
  INITIAL_AUTHENTICATION_RESPONSE,
  useObservabilityToolAuthenticationContext
} from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useTranslation } from 'hooks/use-translation'
import { isPrivateEdition } from 'utils/app-config'
import { getPrimaryLightBackgroundColorsSx } from 'utils/colors'

export const AWSAuthenticationTypeSelect: React.FC = () => {
  const { trans, language } = useTranslation()
  const { control, formState, getValues, reset, setValue } =
    useFormContext<StatusCheckFormValues>()
  const [awsAuthenticationType, awsRoleCreationMethod] =
    useWatch<StatusCheckFormValues>({
      control,
      name: ['awsAuthenticationType', 'awsRoleCreationMethod']
    })

  const { awsIAMRoleMetadata, setAuthenticationResponse } =
    useObservabilityToolAuthenticationContext()

  const shouldShowRoleCreationMethodPrompt =
    awsAuthenticationType === AWSAuthenticationType.IAMRole &&
    !isIamRoleAuthUnavailable(awsIAMRoleMetadata)

  const handleSelectAWSAuthenticationType = (type: AWSAuthenticationType) => {
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)
    setValue('awsAuthenticationType', type)

    if (isPrivateEdition()) {
      // PE only supports the Manual IAM-role creation flow (no CloudFormation).
      setValue('awsRoleCreationMethod', AWSRoleCreationMethod.Manual)
    } else if (!awsRoleCreationMethod) {
      setValue('awsRoleCreationMethod', AWSRoleCreationMethod.CloudFormation)
    }

    // Necessary to preserve the dirty state of the form when switching between authentication types
    if (formState.isDirty) {
      reset(getValues(), { keepValues: true })
    }
  }

  const handleSelectAWSRoleCreationMethod = (method: AWSRoleCreationMethod) => {
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)
    setValue('awsRoleCreationMethod', method)
  }

  const awsAuthenticationTypes = getAWSAuthenticationTypes(language)
  const awsRoleCreationMethods = getAWSRoleCreationMethods()

  return (
    <Stack mb={2} spacing={2}>
      <Typography>
        {trans(
          'status_checks.authentication.aws.authentication_type.select.prompt'
        )}
      </Typography>
      <ButtonGroup
        aria-label={trans(
          'status_checks.authentication.aws.authentication_type.select.aria.label'
        )}
        data-testid="aws-authentication-type-select-button-group"
        variant="outlined"
      >
        {awsAuthenticationTypes.map((type) => {
          const isSelected = awsAuthenticationType === type.value

          return (
            <Button
              key={type.value}
              aria-pressed={isSelected}
              data-testid={`aws-authentication-type-${type.value}`}
              sx={getPrimaryLightBackgroundColorsSx(isSelected)}
              onClick={(e) => {
                e.preventDefault()
                handleSelectAWSAuthenticationType(type.value)
              }}
            >
              {type.label}
            </Button>
          )
        })}
      </ButtonGroup>
      {shouldShowRoleCreationMethodPrompt && (
        <Stack spacing={2}>
          <Typography>
            {trans(
              'status_checks.authentication.aws.role_creation_method.select.prompt'
            )}
          </Typography>
          <ButtonGroup
            aria-label={trans(
              'status_checks.authentication.aws.role_creation_method.select.aria.label'
            )}
            variant="outlined"
          >
            {awsRoleCreationMethods.map((method) => {
              const isSelected = awsRoleCreationMethod === method.value

              return (
                <Button
                  key={method.value}
                  aria-pressed={isSelected}
                  data-testid={`aws-role-creation-method-${method.value}`}
                  sx={getPrimaryLightBackgroundColorsSx(isSelected)}
                  onClick={(e) => {
                    e.preventDefault()
                    handleSelectAWSRoleCreationMethod(method.value)
                  }}
                >
                  {method.label}
                </Button>
              )
            })}
          </ButtonGroup>
        </Stack>
      )}
    </Stack>
  )
}
